"use client";

import { fetchQuotes } from "@capex/services/apiQueries/quotes";
import {
  PaymentPlan,
  PolicyTerm,
  ProductData,
  SignupData,
  QuoteData,
  UserData,
} from "@capex/types";
import { ArrowLongLeftIcon } from "@heroicons/react/24/solid";
import {
  Select,
  Spinner,
  Input,
  Option,
  Checkbox,
} from "@material-tailwind/react";
import { useTransition, useState } from "react";
import { useForm } from "react-hook-form";
import { GenericButton } from "../general/button.component";
import validator from "validator";
import { PageFrameComponent } from "../general/page-frame.component";
import { toast } from "react-toastify";

export const Form2 = ({
  isLoggedIn,
  setStage,
  form1Data,
  productData,
  paymentPlans,
  policyTerms,
  setQuote,
  userData,
}: {
  isLoggedIn: boolean;
  setStage: (stage: number) => void;
  form1Data: SignupData;
  productData: ProductData;
  paymentPlans: PaymentPlan[];
  policyTerms: PolicyTerm[];
  setQuote: (quote: QuoteData) => void;
  userData?: UserData;
}) => {
  const [isPending, startTransition] = useTransition();
  const LifeCoverOptions = [
    { description: "100,000", value: 100000 },
    { description: "250,000", value: 250000 },
    { description: "500,000", value: 500000 },
    { description: "750,000", value: 750000 },
    { description: "1,000,000", value: 1000000 },
    { description: "1,250,000", value: 1250000 },
    { description: "1,500,000", value: 1500000 },
    { description: "1,750,000", value: 1750000 },
    { description: "2,000,000", value: 2000000 },
  ];
  const FlexiPlanOptions = [
    { name: "FLEXI -3 PLAN A-(30/30/50)", value: "IFLEX3A" },
    { name: "FLEXI -3 PLAN B-(25/25/100)", value: "IFLEX3" },
    { name: "FLEXI -3 PLAN C-(30/30/100)", value: "IFLEX3C" },
  ];
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      premium: "",
      frequency: paymentPlans[0].planCode,
      term: policyTerms[0].term,
      agentPhone: "",
      dob: isLoggedIn
        ? userData?.dateOfBirth.split("T")[0]
        : form1Data?.dateOfBirth
        ? form1Data?.dateOfBirth
        : "",
      lifeCover: LifeCoverOptions[0].value,
      productOption: FlexiPlanOptions[0].value,
      agentName: "",
      agentCode: "",
    },
  });
  const millisecondsInEighteenYears = 1000 * 60 * 60 * 24 * 365.25 * 18; // (considering leap years)
  const Timestamp18YearsAgo =
    new Date().getTime() - millisecondsInEighteenYears;

  const onSubmit = handleSubmit(async (data: any) => {
    if (typeof userData?.bvn !== "string" || userData?.bvn.length !== 11) {
      toast.error(
        "Please update your account with your BVN information on the dashboard"
      );
      return;
    }
    startTransition(async () => {
      try {
        const res = await fetchQuotes({
          email: isLoggedIn ? userData?.email : form1Data.email,
          phoneNumber: isLoggedIn ? userData?.mobileNumber : form1Data.phoneNo,
          dateOfBirth: new Date(data.dob).toLocaleDateString("en-US"),
          productCode: productData.productCode,
          policyTerm: data.term,
          paymentTerm: data.frequency,
          sumAssured: 0,
          premiumValue: data.premium,
          ...(data?.agentPhone && { agentPhoneNo: data.agentPhone }),
          ...(productData.productCode === "CSP" && {
            lifeCover: data.lifeCover,
          }),
          ...(productData.productCode === "F3P" && {
            productOption: data.productOption,
          }),
          ...(data?.agentCode && { agentCode: data.agentCode }),
          ...(data?.agentName && { agentName: data.agentName }),
        });
        setQuote(res);
        setStage(3);
      } catch (error) {
        toast.error("An unexpected error occurred.");
      }
    });
  });

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsCheckboxChecked(e.target.checked);
  };

  return (
    <PageFrameComponent>
      <div className="h-full grid grid-cols-1 place-content-center w-full">
        <div className="text-center mb-8">
          <p className="font-medium text-2xl md:text-3xl text-secondary">
            You have chosen to purchase{" "}
            <span className="font-bold capitalize">{productData.Name}</span>
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="flex flex-col items-center gap-8 w-full !text-base"
        >
          <div className="flex flex-col gap-6 w-full md:w-[545px]">
            <div className="flex flex-col gap-1">
              <Input
                variant="static"
                color="black"
                placeholder="How much do you want to contribute?"
                icon={<p className="-ml-5">NGN</p>}
                {...register("premium", {
                  required: true,
                  valueAsNumber: true,
                  validate: (value) => validator.isNumeric(String(value)),
                  min: productData.minimumPremium,
                })}
                {...({} as any)}
              />
              {errors?.premium && (
                <p className="text-red-400 font-medium text-sm">
                  {errors?.premium?.type === "min" &&
                    `Minimum premium for ${
                      productData.Name
                    } is ₦${productData.minimumPremium.toLocaleString()}`}
                  {errors.premium?.type === "validate" &&
                    "Premium must be a number"}
                  {errors.premium?.type === "required" && "Premium is required"}
                </p>
              )}
            </div>
            <Select
              variant="static"
              label="Frequency"
              labelProps={{
                className: "!text-text-mid !text-sm",
              }}
              className="capitalize"
              onChange={(value) => {
                value && setValue("frequency", value);
              }}
              value={paymentPlans[0].planCode}
              {...({} as any)}
            >
              {paymentPlans.map((option) => (
                <Option key={option.planCode} value={option.planCode}>
                  {option.plan}
                </Option>
              ))}
            </Select>
            <Select
              variant="static"
              label="For how many years?"
              labelProps={{
                className: "!text-text-mid !text-sm",
              }}
              className="capitalize"
              onChange={(value) => value && setValue("term", value)}
              value={policyTerms[0].term}
              {...({} as any)}
            >
              {policyTerms.map((option) => (
                <Option key={option.term} value={option.term}>
                  {option.termDescription}
                </Option>
              ))}
            </Select>
            {productData.productCode === "CSP" && (
              <Select
                variant="static"
                label="Life cover"
                labelProps={{
                  className: "!text-text-mid !text-sm",
                }}
                className="capitalize"
                onChange={(option) =>
                  option && setValue("lifeCover", Number(option))
                }
                value={String(LifeCoverOptions[0].value)}
                {...({} as any)}
              >
                {LifeCoverOptions.map((option, index) => (
                  <Option key={index} value={String(option.value)}>
                    ₦ {option.description}
                  </Option>
                ))}
              </Select>
            )}
            {productData.productCode === "F3P" && (
              <Select
                variant="static"
                label="Product Option"
                labelProps={{
                  className: "!text-text-mid !text-sm",
                }}
                className="capitalize"
                onChange={(value) => {
                  value && setValue("productOption", value);
                }}
                value={FlexiPlanOptions[0].value}
                {...({} as any)}
              >
                {FlexiPlanOptions.map((option, index) => (
                  <Option key={index} value={option.value}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            )}
            <div className="flex flex-col gap-1">
              <Input
                variant="static"
                label="Date of birth (above 18)"
                labelProps={{
                  className: "!text-text-mid !text-sm",
                }}
                placeholder="dd/mm/yyyy"
                className="text-text-dark"
                color="black"
                type="date"
                {...register("dob", {
                  required: true,
                  validate: (value) =>
                    //@ts-ignore
                    new Date(value) !== "Invalid Date" &&
                    //@ts-ignore
                    new Date(value).getTime() < Timestamp18YearsAgo,
                })}
                {...({} as any)}
              />
              {errors?.dob && (
                <p className="text-red-400 font-medium text-sm">
                  {errors.dob?.type === "validate" &&
                    "Date of Birth must be a validate date and you must be above 18 years"}
                  {errors.dob?.type === "required" &&
                    "Date of Birth is required"}
                </p>
              )}
            </div>
            <div className="-mt-2.5">
              <Checkbox
                className="border-disabled"
                checked={isCheckboxChecked}
                onChange={handleCheckboxChange}
                label={
                  <p className="flex flex-wrap text-[15px] text-text-darker ml-1">
                    Were you referred by an Agent? (Yes)
                  </p>
                }
                {...({} as any)}
              />
            </div>
            {isCheckboxChecked && (
              <>
                <div className="flex flex-col gap-1 -mt-6">
                  <Input
                    variant="static"
                    color="black"
                    labelProps={{
                      className: "!text-text-mid !text-sm",
                    }}
                    placeholder="Agent's Name (Optional)"
                    {...register("agentName", {
                      required: false,
                      minLength: 2,
                      maxLength: 50,
                    })}
                    {...({} as any)}
                  />
                  {errors?.agentName && (
                    <p className="text-red-400 font-medium text-sm">
                      {errors?.agentName?.type === "minLength" &&
                        "First Name must contain at least 2 characters"}
                      {errors.agentName?.type === "maxLength" &&
                        "First Name must contain maximum of 50 characters"}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <div className="flex flex-col gap-1 -mt-2">
                    <Input
                      variant="static"
                      color="black"
                      labelProps={{
                        className: "!text-text-mid !text-sm",
                      }}
                      placeholder="Agent's Phone Number (Optional)"
                      {...register("agentPhone", {
                        validate: (value) =>
                          value === "" ? true : validator.isMobilePhone(value),
                      })}
                      {...({} as any)}
                    />
                    {errors?.agentPhone && (
                      <p className="text-red-400 font-medium text-sm">
                        {errors.agentPhone?.type === "validate" &&
                          "Agent's Phone Number must be valid"}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 -mt-2">
                    <Input
                      variant="static"
                      color="black"
                      labelProps={{
                        className: "!text-text-mid !text-sm",
                      }}
                      placeholder="Agent's Code (Optional)"
                      {...register("agentCode", {
                        required: false,
                        minLength: 1,
                        maxLength: 20,
                      })}
                      {...({} as any)}
                    />
                    {errors?.agentCode && (
                      <p className="text-red-400 font-medium text-sm">
                        Agents Code must be between 1 and 20 characters
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
            <div
              className={`flex w-full mt-3 ${
                isLoggedIn ? "justify-center" : "justify-between"
              }`}
            >
              {!isLoggedIn && (
                <GenericButton
                  className="bg-accent flex justify-center items-center gap-x-3 !w-[155px] md:!w-[230px] lg:!w-[245px]"
                  onClick={() => setStage(1)}
                >
                  <ArrowLongLeftIcon className="w-6 h-6" />
                  Back
                </GenericButton>
              )}

              <GenericButton
                type="submit"
                className="bg-primary flex justify-center items-center gap-x-3 !w-[155px] md:!w-[230px] lg:!w-[245px]"
              >
                {isPending ? (
                  <div className="flex justify-center items-center">
                    <Spinner className="h-6 w-6 text-white" {...({} as any)} />
                  </div>
                ) : (
                  "Get Quote"
                )}
              </GenericButton>
            </div>
          </div>
        </form>
      </div>
    </PageFrameComponent>
  );
};
